/**
 * Patient-related utility functions
 */

/**
 * Build a composite patient ID (pharmacyId_patientId).
 * If the ID already contains an underscore, returns it as-is.
 */
export function buildCompositePatientId(
  patientId: string | null | undefined,
  pharmacyId: string | null | undefined,
): string {
  if (!patientId) return '';
  if (patientId.includes('_')) return patientId;
  return pharmacyId ? `${pharmacyId}_${patientId}` : patientId;
}

/**
 * Parse a composite patient ID into its parts.
 */
export function parseCompositePatientId(
  compositeId: string | undefined,
  fallbackPharmacyId?: string,
): { pharmacyId: string | null; patientNumber: string | null; compositeId: string | null } {
  if (!compositeId) return { pharmacyId: null, patientNumber: null, compositeId: null };

  if (compositeId.includes('_')) {
    const [pharmacyId, patientNumber] = compositeId.split('_');
    return { pharmacyId, patientNumber, compositeId };
  }

  return {
    pharmacyId: fallbackPharmacyId || null,
    patientNumber: compositeId,
    compositeId: fallbackPharmacyId ? `${fallbackPharmacyId}_${compositeId}` : compositeId,
  };
}

/** A labelled program-specific patient identifier, as shown on the patient profile. */
export interface PatientCode {
  /** Short label, e.g. "Kroll ID / Quick Code". */
  label: string;
  /** The identifier itself. Never empty — codes without a value are omitted. */
  value: string;
  /** Hover text explaining what the code is for. */
  tooltip: string;
}

/**
 * Build the list of program-specific patient identifiers shown on the Patient 360
 * header ribbon — Kroll ID / Quick Code, MRN, Health Card (GP-1044).
 *
 * Single source of truth so every surface that needs "the patient's codes"
 * (patient profile, packaging window) labels and derives them identically.
 * Codes with no value are omitted rather than defaulted — a blank code is never
 * substituted with a placeholder that could be mistaken for a real identifier.
 *
 * @param compositePatientId Order/patient composite id (`pharmacyId_krollPatientId`);
 *   the Kroll ID / Quick Code is its patient segment.
 * @param patient Patient record from Kroll (see `usePatientBasicInfo`), if loaded.
 */
export function buildPatientCodes(
  compositePatientId: string | null | undefined,
  patient?: { mrn?: string | null; healthCardNumber?: string | null } | null,
): PatientCode[] {
  const { patientNumber } = parseCompositePatientId(compositePatientId || undefined);

  const codes: Array<Omit<PatientCode, 'value'> & { value: string | null | undefined }> = [
    {
      label: 'Kroll ID / Quick Code',
      value: patientNumber,
      tooltip: 'Kroll ID / Quick Code — use this to pull the patient up in Kroll',
    },
    {
      label: 'MRN',
      value: patient?.mrn,
      tooltip: 'MRN — the patient’s medical record number in Kroll',
    },
    {
      label: 'Health Card',
      value: patient?.healthCardNumber,
      tooltip: 'Health Card — the patient’s provincial health card number',
    },
  ];

  return codes
    .filter((code): code is PatientCode => typeof code.value === 'string' && code.value.trim() !== '')
    .map((code) => ({ ...code, value: code.value.trim() }));
}

/**
 * Extract patient initials from full name
 * @param patientName Full name in format "FirstName LastName", "FirstName MiddleName LastName", or "LastName, FirstName" (Kroll format)
 * @returns Two-letter initials (e.g., "JD" for "John Doe"), or "??" if invalid
 *
 * @example
 * getPatientInitials("John Doe") // Returns "JD"
 * getPatientInitials("Doe, John") // Returns "JD"
 * getPatientInitials("Jane Mary Smith") // Returns "JS"
 * getPatientInitials("Madonna") // Returns "M?"
 * getPatientInitials("") // Returns "??"
 */
export function getPatientInitials(patientName: string | null | undefined): string {
  // Handle null, undefined, or empty string
  if (!patientName || typeof patientName !== 'string' || !patientName.trim()) {
    return '??';
  }

  let nameParts: string[];

  // Handle "LastName, FirstName" format from Kroll
  if (patientName.includes(',')) {
    const parts = patientName.split(',').map(p => p.trim());
    // Reverse order: "LastName, FirstName" → ["FirstName", "LastName"]
    nameParts = parts.length >= 2 ? [parts[1], parts[0]] : parts;
  } else {
    // Split name into parts and filter out empty strings
    nameParts = patientName.trim().split(/\s+/).filter(part => part.length > 0);
  }

  // No valid name parts
  if (nameParts.length === 0) {
    return '??';
  }

  // Single name only (e.g., "Madonna")
  if (nameParts.length === 1) {
    const firstLetter = nameParts[0][0]?.toUpperCase() || '?';
    return `${firstLetter}?`;
  }

  // Two or more names - use first letter of first name + first letter of last name
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];

  const firstInitial = firstName[0]?.toUpperCase() || '?';
  const lastInitial = lastName[0]?.toUpperCase() || '?';

  return `${firstInitial}${lastInitial}`;
}

/**
 * Format patient name for display
 * @param firstName Patient's first name
 * @param lastName Patient's last name
 * @param middleName Optional middle name
 * @returns Formatted full name
 */
export function formatPatientName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  middleName?: string | null
): string {
  const parts: string[] = [];

  if (firstName?.trim()) parts.push(firstName.trim());
  if (middleName?.trim()) parts.push(middleName.trim());
  if (lastName?.trim()) parts.push(lastName.trim());

  return parts.join(' ') || 'Unknown Patient';
}
